import { useRouter } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { showCreateWorkspaceError } from '@nx-frontend/shared/actions';
import { axiosClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { useBuyPortalAddon, useOpenPaywallPopupNew } from '@nx-frontend/shared/hooks';
import { needOwnerToPurchasePortalPopup } from '@nx-frontend/shared/popups/need-owner-to-purchase-portal-popup';
import { $isFreePlan } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization';
import { $orgPortals } from '@nx-frontend/shared/store/org-portals';
import { $opCanCreatePortal, $opCanManageBilling } from '@nx-frontend/shared/store/org-role';
import { $orgUsage } from '@nx-frontend/shared/store/org-usage';
import { $portalsUsage } from '@nx-frontend/shared/store/portal-usage';
import { $isBusinessTrialActive } from '@nx-frontend/shared/store/trial';
import {
  DialogPopupPayload,
  PaywallPopupPayload,
  PopupType,
  PortalCreateWorkspaceParams,
  Workspace,
} from '@nx-frontend/shared/types';
import { getPortalWizardUrl } from '@nx-frontend/shared/utils';

export const useNavigateToCreatePortal = () => {
  const [openModal, closeModal] = useEvent([openModalEvent, closeModalEvent]);
  const [
    currentOrganization,
    opCanCreatePortal,
    orgPortals,
    portalsUsage,
    opCanManageBilling,
    isFreePlan,
    isBusinessTrialActive,
  ] = useUnit([
    $currentOrganization,
    $opCanCreatePortal,
    $orgPortals,
    $portalsUsage,
    $opCanManageBilling,
    $isFreePlan,
    $isBusinessTrialActive,
  ]);
  const router = useRouter();
  const openPaywallPopup = useOpenPaywallPopupNew();
  const buyPortalAddon = useBuyPortalAddon();

  return ({ workspaceId }: { workspaceId: string }) => {
    const wizardUrl = getPortalWizardUrl(generalEnv.appNimbusDomain || generalEnv.nimbusDomain, workspaceId);

    if (!portalsUsage) {
      console.warn('Empty portalsUsage');
      return;
    }

    if (!orgPortals) {
      console.warn('Empty orgPortals');
      return;
    }

    const workspacePortal = orgPortals.find((portal) => portal.workspaceId === workspaceId);
    if (workspacePortal) {
      console.log('Workspace already has portal');
      return;
    }

    const noPermissionsPopupPayload: DialogPopupPayload = {
      payloadType: PopupType.DIALOG,
      text: 'Only the organization owner or managers can launch the portal setup.',
      buttons: [
        {
          text: 'OK',
        },
      ],
    };

    const noPermissionsPopup = {
      title: 'No permissions to launch the portal',
      content: PopupType.DIALOG,
      showCloseButton: true,
      closeButtonSize: 18,
      payload: noPermissionsPopupPayload,
    };

    const workspaceHasPortalPopupPayload: DialogPopupPayload = {
      payloadType: PopupType.DIALOG,
      text: 'Do you want to create a new workspace and create a portal in it?',
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Create a workspace',
          main: true,
          onClick: () => {
            router.push(`/space/settings/org/${currentOrganization.globalId}/ws/add`);
            closeModal();
          },
        },
      ],
    };

    const workspaceHasPortalPopup = {
      title: 'The workspace already has a portal',
      content: PopupType.DIALOG,
      showCloseButton: true,
      closeButtonSize: 18,
      payload: workspaceHasPortalPopupPayload,
    };

    if (!opCanCreatePortal) {
      openModal(noPermissionsPopup);
      return false;
    }

    if (workspacePortal) {
      openModal(workspaceHasPortalPopup);
      return false;
    }

    const { active, limit } = portalsUsage;

    const needToPurchaseSubscription = () => {
      const popupPayload: PaywallPopupPayload = {
        payloadType: PopupType.PAYWALL,
        returnUrl: wizardUrl,
      };
      if (opCanManageBilling) {
        openPaywallPopup({ payload: popupPayload });
      } else {
        openModal(needOwnerToPurchasePortalPopup);
      }
    };

    if (active >= limit) {
      if (isFreePlan) {
        needToPurchaseSubscription();
        return false;
      }

      if (!opCanCreatePortal) {
        openModal(noPermissionsPopup);
        return false;
      }

      if (isBusinessTrialActive) {
        needToPurchaseSubscription();
      } else {
        buyPortalAddon();
      }
      return false;
    }

    if (wizardUrl) {
      window.open(wizardUrl, '_self');
    }

    return true;
  };
};

export const useNewPortalWorkspaceClickAction = () => {
  const navigateToCreatePortal = useNavigateToCreatePortal();
  const [openModal] = useEvent([openModalEvent]);
  const [orgPortals, portalsUsage, orgUsage, currentOrganization, opCanCreatePortal] = useUnit([
    $orgPortals,
    $portalsUsage,
    $orgUsage,
    $currentOrganization,
    $opCanCreatePortal,
  ]);

  return async ({ workspace }: { workspace: Workspace | null }) => {
    const workspacePortal = orgPortals.find((portal) => portal.workspaceId === workspace?.globalId);

    if (workspacePortal) {
      console.log('Workspace already has portal');
      return;
    }

    if (!portalsUsage) {
      console.warn('Empty portalsUsage');
      return;
    }

    let currentWorkspaces = 0;
    if (orgUsage && orgUsage.data && orgUsage.data.workspaces) {
      currentWorkspaces = orgUsage.data.workspaces.current;
    }
    const createWorkspace: PortalCreateWorkspaceParams = {
      shouldByPortalAddon: false,
      orgId: currentOrganization.globalId,
      title: `${currentOrganization.title} Portal ${currentWorkspaces + 1}`,
    };
    let preparedWorkspace: Workspace | null = workspace;
    if (!preparedWorkspace) {
      if (opCanCreatePortal) {
        if (portalsUsage.active < portalsUsage.limit) {
          try {
            preparedWorkspace = (
              await axiosClient.post<Workspace>(`/v2/api/workspaces/create`, {
                orgId: createWorkspace.orgId,
                title: createWorkspace.title,
                skipLimits: true,
              })
            ).data;
          } catch (err) {
            showCreateWorkspaceError(openModal);
            return;
          }
        } else {
          createWorkspace.shouldByPortalAddon = true;
        }
      }
    }

    if (!createWorkspace.shouldByPortalAddon && (!preparedWorkspace || !preparedWorkspace.globalId)) {
      console.warn('Empty workspaceId');
      return;
    }

    navigateToCreatePortal({
      workspaceId: createWorkspace.shouldByPortalAddon ? '' : preparedWorkspace ? preparedWorkspace.globalId : '',
    });
  };
};
